import { TreeNode } from '../components/treeSelector';
import { CategoryItem, DepartmentItem } from '../types/category';
import { CategoryCombinedTableItem } from '../components/categoryTable';

type LookupType<T> = {
  [id in CategoryCombinedTableItem['id']]: T;
};
export function transformToTableList(
  arr: (CategoryItem | DepartmentItem)[]
): CategoryCombinedTableItem[] {
  const result: CategoryCombinedTableItem[] = [];
  const lookup: LookupType<CategoryCombinedTableItem> = {};

  // 首先，创建一个查找表，但忽略isDeleted为true的元素
  arr.forEach((item) => {
    if (!item.isDeleted) {
      lookup[item.id] = { ...item, children: [] };
    }
  });

  // 然后，遍历查找表，根据parentId构建树
  Object.values(lookup).forEach((item) => {
    if (item.parentId === null) {
      // 如果parentId为null，说明是顶级元素，直接加入结果数组
      result.push(item);
    } else if (lookup[item.parentId]) {
      // 否则，将其添加到父元素的children数组中
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (lookup[item.parentId] as any).children.push(item);
    }
  });

  return result;
}
export function transformToTreeNodes(nodes: (CategoryItem | DepartmentItem)[]): TreeNode[] {
  // 过滤掉 isDeleted 为 true 的节点
  const filteredNodes = nodes.filter((node) => !node.isDeleted);

  // 创建一个 Map 来存储每个节点的子节点
  const map: { [key: string]: TreeNode } = {};

  // 初始化每个节点
  filteredNodes.forEach((node) => {
    map[node.id] = { id: node.id, name: node.name, children: [] };
  });

  const tree: TreeNode[] = [];

  filteredNodes.forEach((node) => {
    if (node.parentId === null) {
      // 如果没有父节点，直接添加到树根
      tree.push(map[node.id]);
    } else if (map[node.parentId]) {
      // 如果有父节点，添加到父节点的 children 中
      map[node.parentId].children!.push(map[node.id]);
    }
  });

  // 移除没有子节点的 children 属性
  Object.values(map).forEach((node) => {
    if (node.children!.length === 0) {
      delete node.children;
    }
  });

  return tree;
}

/**
 * 获取某部门的所有子部门（递归）
 * @param departments 所有部门列表
 * @param parentId 父部门ID
 * @returns 所有子部门（包括多级）
 */
export function getAllSubDepartments(
  departments: DepartmentItem[],
  parentId: string | number
): DepartmentItem[] {
  const subs: DepartmentItem[] = [];
  function findChildren(pid: string | number) {
    departments.forEach((dept) => {
      if (dept.parentId === pid) {
        subs.push(dept);
        findChildren(dept.id);
      }
    });
  }
  findChildren(parentId);
  return subs;
}

/**
 * 获取某部门的直接下级部门（非递归）
 * @param departments 所有部门列表
 * @param parentId 父部门ID
 * @returns 直接下级部门（只包括一级下级）
 */
export function getDirectSubDepartments(
  departments: DepartmentItem[],
  parentId: string | number
): DepartmentItem[] {
  return departments.filter((dept) => dept.parentId === parentId);
}

export const isGroupNode = (dept?: { tag?: string } | null) => dept?.tag === 'Group';

export function getDepartmentChainIds(
  departments: DepartmentItem[],
  id?: string | null
): Set<string> {
  const byId = new Map(departments.map((dept) => [dept.id, dept]));
  const ids = new Set<string>();
  let current = id ?? undefined;
  while (current && !ids.has(current)) {
    ids.add(current);
    current = byId.get(current)?.parentId ?? undefined;
  }
  return ids;
}

export function isSameOrSubDepartment(
  departments: DepartmentItem[],
  parentId?: string | null,
  id?: string | null
): boolean {
  return !!parentId && getDepartmentChainIds(departments, id).has(parentId);
}

export type DepartmentTreeOption = {
  title: string;
  value: string;
  selectable?: boolean;
  children?: DepartmentTreeOption[];
};

export function toDepartmentTreeOptions(
  departments: DepartmentItem[],
  isSelectable?: (dept: DepartmentItem) => boolean
): DepartmentTreeOption[] {
  const active = departments.filter((dept) => !dept.isDeleted);
  const ids = new Set(active.map((dept) => dept.id));
  const childrenMap = new Map<string | null, DepartmentItem[]>();
  active.forEach((dept) => {
    const key = dept.parentId && ids.has(dept.parentId) ? dept.parentId : null;
    const siblings = childrenMap.get(key);
    if (siblings) {
      siblings.push(dept);
    } else {
      childrenMap.set(key, [dept]);
    }
  });
  const build = (key: string | null): DepartmentTreeOption[] =>
    (childrenMap.get(key) ?? []).map((dept) => {
      const children = build(dept.id);
      return {
        title: dept.name,
        value: dept.id,
        ...(isSelectable && { selectable: isSelectable(dept) }),
        ...(children.length > 0 && { children }),
      };
    });
  return build(null);
}
